import { useId, useState, type ReactNode } from "react";

import { dataHora, num } from "../../lib/format";

export interface SerieDef {
  chave: string;
  rotulo: string;
  cor: string;
}

/** Altura do desenho **mais** a banda do eixo x, para o cartão não ganhar scroll. */
export const ALTURA = 172;

interface MolduraProps {
  titulo: string;
  unidade: string;
  /**
   * Código da boia, quando o gráfico é de **uma** só.
   *
   * Vai no título e não na legenda porque vale para as duas vistas: a tabela
   * fica debaixo do mesmo cabeçalho e herda a identificação. Sem isto, com
   * várias boias em comparação nos gráficos de cima, estes dois mostravam uma
   * boia sem dizer qual — e a leitura natural era "são todas".
   */
  boia?: string | undefined;
  nota?: string | undefined;
  series: SerieDef[];
  /** Linhas da tabela equivalente. O gráfico nunca é a única via para o valor. */
  tabela: { cabecalho: string[]; linhas: (string | null)[][] };
  children: ReactNode;
}

/**
 * Título, legenda, alternador de tabela e o gráfico.
 *
 * A tabela não é um extra: um gráfico onde só a passagem do rato revela os
 * valores deixa de fora quem navega por teclado ou leitor de ecrã. Aqui a
 * tabela tem exatamente os mesmos números.
 *
 * A legenda aparece a partir de **duas** séries. Com uma só, o título já diz
 * o que a linha é, e uma caixa de legenda seria ruído.
 */
export function Moldura({
  titulo,
  unidade,
  boia,
  nota,
  series,
  tabela,
  children,
}: MolduraProps): ReactNode {
  const [comoTabela, setComoTabela] = useState(false);
  const id = useId();

  return (
    <section className="grafico" aria-labelledby={id}>
      <div className="grafico-topo">
        <h4 id={id}>
          {titulo}
          {boia !== undefined && <span className="so-boia">{boia}</span>}
        </h4>
        <span className="unidade">{unidade}</span>
        <button
          type="button"
          className="alternar"
          aria-pressed={comoTabela}
          onClick={() => setComoTabela((v) => !v)}
        >
          {comoTabela ? "Gráfico" : "Tabela"}
        </button>
      </div>

      {nota !== undefined && <p className="nota">{nota}</p>}

      {series.length > 1 && (
        <div className="legenda">
          {series.map((s) => (
            <span key={s.chave}>
              <i style={{ background: s.cor }} aria-hidden="true" />
              {s.rotulo}
            </span>
          ))}
        </div>
      )}

      {comoTabela ? <Tabela {...tabela} /> : children}
    </section>
  );
}

function Tabela({
  cabecalho,
  linhas,
}: {
  cabecalho: string[];
  linhas: (string | null)[][];
}): ReactNode {
  if (linhas.length === 0) return <p className="vazio-msg">Sem dados no intervalo.</p>;
  return (
    <div className="tabela-envolvente">
      <table className="tabela">
        <thead>
          <tr>
            {cabecalho.map((c) => (
              <th key={c} scope="col">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {linhas.map((linha, i) => (
            <tr key={i}>
              {linha.map((celula, j) =>
                j === 0 ? (
                  <th key={j} scope="row">
                    {celula}
                  </th>
                ) : (
                  <td key={j} className={celula === null ? "sem" : undefined}>
                    {celula ?? "sem dados"}
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface ItemDica {
  cor: string;
  rotulo: string;
  valor: number | null;
}

/**
 * Conteúdo da dica.
 *
 * Os números vão em tinta de texto, não na cor da série — a cor vive no quadrado
 * ao lado, que é quem carrega a identidade.
 */
export function corpoDica(
  instante: number,
  itens: ItemDica[],
  unidade: string,
  casas: number,
): ReactNode {
  return (
    <div className="dica">
      <div className="quando">{dataHora(new Date(instante).toISOString())}</div>
      {itens.map((item) => (
        <div className="linha" key={item.rotulo}>
          <i style={{ background: item.cor }} aria-hidden="true" />
          <span>{item.rotulo}</span>
          <strong>
            {item.valor === null ? "sem dados" : `${num(item.valor, casas)} ${unidade}`}
          </strong>
        </div>
      ))}
    </div>
  );
}

export function SemDados(): ReactNode {
  return <p className="vazio-msg">Sem dados no intervalo.</p>;
}
